import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { Component, Inject, inject, OnInit, ViewChild } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import {
  Authorization,
  ConsumerEntity,
  NlpSettings,
  PlayerSettings,
  PlayerTheme,
  SavedLearnObjectType
} from '../consumer.entity';
import { urlValidator } from '../../core/util';
import { NoAuthorization } from '../noAuthorization.model';
import { defaultTheme } from '../../core/types';
import { BasicAuth } from '../basicAuth.model';
import { SubjectJsonModel } from '../../subject/subject.json.model';
import { Store } from '@ngxs/store';
import { AppSelectors } from '../../app.state';
import { MatDivider } from '@angular/material/divider';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { NgxColorsModule } from 'ngx-colors';
import { ConsumerInfoComponent } from './consumer-info/consumer-info.component';
import { ConsumerPlayerConfigComponent } from './consumer-player-config/consumer-player-config.component';
import { ConsumerMetadataComponent } from './consumer-metadata/consumer-metadata.component';
import { GlobalMetadataFactoryService } from '../../global-metadata/services/global-metadata.factory.service';
import {
  ConsumerObjectTypeMetadataComponent
} from './consumer-object-type-metadata/consumer-object-type-metadata.component';

@Component({
  selector: 'pixl-form-dialog-consumer',
  templateUrl: './form-dialog-consumer.component.html',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogContent,
    MatDialogTitle,
    MatDialogActions,
    ReactiveFormsModule,
    MatButton,
    FormsModule,
    MatDivider,
    MatTab,
    MatTabGroup,
    NgxColorsModule,
    ConsumerInfoComponent,
    ConsumerPlayerConfigComponent,
    ConsumerMetadataComponent,
    ConsumerObjectTypeMetadataComponent
  ],
  styleUrls: ['./form-dialog-consumer.component.scss']
})
export class FormDialogConsumerComponent implements OnInit {
  private fb = inject(FormBuilder);
  private globalMetadataFactory = inject(GlobalMetadataFactoryService);
  private readonly store = inject(Store);
  public resetTheme!: PlayerTheme;
  private appState$ = this.store.select(AppSelectors.appState);
  private dialogRef = inject(MatDialogRef<FormDialogConsumerComponent, ConsumerEntity>);

  @ViewChild(ConsumerObjectTypeMetadataComponent)
  consumerObjectTypeMetadata!: ConsumerObjectTypeMetadataComponent;

  public consumerForm!: FormGroup;
  public competencies: SubjectJsonModel[] = [];

  private consumerAuth!: Authorization;
  private selectedAuth!: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data?: Partial<ConsumerEntity>
  ) {
    this.consumerAuth = data?.authorization ?? new NoAuthorization();
    this.selectedAuth = this.consumerAuth._t;
    this.initForm();
  }

  ngOnInit(): void {
    this.appState$.subscribe(({ isLoaded, subjects }) => {
      if (isLoaded) {
        this.competencies = subjects;
        this.initSubjectConfigs();
      }
    });
  }

  private initSubjectConfigs(): void {
    const subjectConfigsFArray = this.consumerForm.get('playerSettings.subjectConfigs') as FormArray;
    subjectConfigsFArray.clear();

    const initialSubjectConfigs = this.data?.playerSettings?.subjectConfigs || [];

    const rootCompetencies = this.competencies.filter(competency => competency.root === true);

    rootCompetencies.forEach(competency => {
      const subjectIdToUse = (competency.treeId && competency.treeId.trim() !== '') ? competency.treeId : '0';

      const existingConfig = initialSubjectConfigs.find(
        config => config.subjectId === subjectIdToUse
      );

      const fontId = existingConfig?.fontId || 'def';

      subjectConfigsFArray.push(this.fb.group({
        subjectId: [subjectIdToUse, Validators.required],
        fontId: [fontId],
      }));
    });
  }

  private initForm(): void {
    const data: Partial<ConsumerEntity> = this.data ?? {};

    const nlpSettings: NlpSettings = data.nlpSettings ?? {};
    const playerSettings: PlayerSettings = data.playerSettings ?? {};
    const savedLearnObjectTypeDescriptions: SavedLearnObjectType[] = (data?.learnObjectTypeDescriptions ?? []).map(t => {
      if (typeof t === 'string') {
        return { label: t, metadata: [] };
      }
      else {
        return {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          label: (t as any).label ?? (t as any).name,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          metadata: (t as any).metadata ?? []
        };
      }
    });

    this.consumerForm = this.fb.group({
      name: [data.name, [Validators.required]],
      description: [data.description, [Validators.required]],
      kiService: data.kiService,
      baseColor: data.baseColor,
      playerFilesFolder: data.playerFilesFolder,
      url: [data.url, [Validators.required, urlValidator]],
      version: data.version,
      authorization: this.selectedAuth,
      availableClasses: this.fb.array([]),
      nlpSettings: this.fb.group({
        nlpFeedbackLanguage: nlpSettings.nlpFeedbackLanguage ?? 'de',
        nlpFeedbackCorrectnessThreshold: nlpSettings.nlpFeedbackCorrectnessThreshold,
      }),
      playerSettings: this.fb.group({
        selfJudgementPopup: playerSettings.selfJudgementPopup,
        dontKnowBtn: playerSettings.dontKnowBtn,
        readoutExerciseText: playerSettings.readoutExerciseText,
        randomOrderOfTestSequences: playerSettings.randomOrderOfTestSequences,
        textSelectionEnabled: playerSettings.textSelectionEnabled,
        emptyAnswerWarningPopup: playerSettings.emptyAnswerWarningPopup,
        checkAnswerPopup: playerSettings.checkAnswerPopup,
        showNoteButton: playerSettings.showNoteButton,
        showBottomPart: playerSettings.showBottomPart,
        showSelfRating: playerSettings.showSelfRating,
        showExerciseSequenceTabs: playerSettings.showExerciseSequenceTabs,
        hideExerciseHeadline: playerSettings.hideExerciseHeadline,
        maxTries: playerSettings.maxTries?.toString(),
        retryMode: playerSettings.retryMode,
        hideResetExerciseButton: playerSettings.hideResetExerciseButton,
        showCompletionIcons: playerSettings.showCompletionIcons,
        tabLetters: playerSettings.tabLetters,
        disableExerciseFeedback: playerSettings.disableExerciseFeedback,
        flattenExercises: playerSettings.flattenExercises,
        footerInfo: playerSettings.footerInfo,
        theme: this.fb.group({
          primaryColor: playerSettings.theme?.primaryColor ?? defaultTheme.primaryColor,
          secondaryColor: playerSettings.theme?.secondaryColor ?? defaultTheme.secondaryColor,
          tertiaryColor: playerSettings.theme?.tertiaryColor ?? defaultTheme.tertiaryColor,
          placeholderColor: playerSettings.theme?.placeholderColor ?? defaultTheme.placeholderColor,
          hoverColor: playerSettings.theme?.hoverColor ?? defaultTheme.hoverColor,
          drawColor: playerSettings.theme?.drawColor ?? defaultTheme.drawColor,
          drawHighlightColor: playerSettings.theme?.drawHighlightColor ?? defaultTheme.drawHighlightColor,
          compareSolutionsBgColor:
            playerSettings.theme?.compareSolutionsBgColor ?? defaultTheme.compareSolutionsBgColor,
        }),
        allowedFrontendOrigins: this.fb.array([]),
        subjectConfigs: this.fb.array([]),
      }),
      consumerMetadata: this.fb.array(
        (data.consumerMetadata ?? []).map(m => this.globalMetadataFactory.createMetadataGroup(m))
      ),
      learnObjectTypeDescriptions: this.fb.array(
        savedLearnObjectTypeDescriptions.map(t =>
          this.globalMetadataFactory.createObjectTypeMetadataGroup(t)
        )
      ),
    });

    this.createResetTheme();
    this.initSubjectConfigs();
  }

  private createResetTheme(): void {
    const data: Partial<ConsumerEntity> = this.data ?? {};

    const playerSettingsTheme: PlayerTheme = data.playerSettings?.theme ?? {};

    this.resetTheme = {
      ...defaultTheme,
      ...playerSettingsTheme,
    };
  }

  public onSave(): void {
    if (!this.consumerForm.valid) {
      this.logFormErrors();
      return this.consumerForm.markAllAsTouched();
    }

    const formValue = this.consumerForm.value;

    // Cleaned learnObjectTypeDescriptions
    const cleanedLearnObjectTypeDescriptions =
      this.consumerObjectTypeMetadata.prepareForSave();

    // Build the updated ConsumerEntity instance
    const updatedConsumer = Object.assign(
      new ConsumerEntity(),
      { id: this.data?.id },
      formValue,
      {
        authorization: formValue.authorization === 'BasicAuth' ? new BasicAuth() : new NoAuthorization(),
        playerSettings: formValue.playerSettings ? { ...formValue.playerSettings } : undefined,
        nlpSettings: formValue.nlpSettings ? { ...formValue.nlpSettings } : undefined,
        availableClasses: formValue.availableClasses || [],
        consumerMetadata: formValue.consumerMetadata || [],
        learnObjectTypeDescriptions: cleanedLearnObjectTypeDescriptions,
        playerFilesFolder: formValue.playerFilesFolder,
      }
    );

    this.dialogRef.close(updatedConsumer);
  }

  public onCancel(): void {
    this.dialogRef.close();
  }

  public get subjectConfigs(): FormArray {
    return (this.consumerForm.get('playerSettings') as FormGroup).get('subjectConfigs') as FormArray;
  }

  private logFormErrors(): void {
    console.log('Formularfehler (Gesamt):', this.consumerForm.errors);

    Object.keys(this.consumerForm.controls).forEach(key => {
      const control = this.consumerForm.get(key);
      if (control && control.invalid) {
        console.log(`Feld '${key}' ist ungültig. Fehler:`, control.errors);
      }
    });

    const playerSettingsGroup = this.consumerForm.get('playerSettings') as FormGroup;
    if (playerSettingsGroup && playerSettingsGroup.invalid) {
      console.log('PlayerSettings Gruppe ist ungültig.');
      Object.keys(playerSettingsGroup.controls).forEach(key => {
        const control = playerSettingsGroup.get(key);
        if (control && control.invalid) {
          console.log(`  PlayerSettings-Feld '${key}' ist ungültig. Fehler:`, control.errors);
        }
      });

      const subjectConfigsArray = playerSettingsGroup.get('subjectConfigs') as FormArray;
      if (subjectConfigsArray && subjectConfigsArray.invalid) {
        console.log('  SubjectConfigs Array ist ungültig.');
        subjectConfigsArray.controls.forEach((control, index) => {
          if (control.invalid) {
            console.log(`    SubjectConfig an Index ${index} ist ungültig. Fehler:`, control.errors);
          }
        });
      }
    }
  }
}
